/**
 * Pure rules for the KYC bulk actions (Sync all, Import, Refresh documents).
 * Writes go through applyKycProfileFill (fill-only); this module decides WHO and WITH WHAT.
 */
import { KYC_DOC_STATUS, CONFIDENCE, inferDocTypeFromPath, normalizeNameKey } from "./kycConstants";
import { parseDateLoose, genericFieldLabel } from "./kycProfileFill";

/**
 * A new KYC profile may be created only for numeric account status 0 or 1.
 * Text, null, blank, boolean and any other number (including 2 and 3) do not create.
 * Number(null) and Number("") are 0 in JavaScript, so those are rejected before Number().
 */
export function canCreateProfileForStatus(status) {
  if (status === null || status === undefined || status === "") return false;
  if (typeof status === "boolean") return false;
  const s = Number(status);
  if (!Number.isInteger(s)) return false;
  return s === 0 || s === 1;
}

export function inferExpiryDateFromFilename(filePath = "") {
  const lower = String(filePath || "").toLowerCase();
  if (!/(expir|expiry|valid|venc|passport|license|id|identit)/.test(lower)) return null;
  return parseDateLoose(filePath);
}

export function entryName(entry) {
  return entry?.display_name || entry?.folder_name || "";
}

export function orphanIdForEntry(entry) {
  return `orphan_${normalizeNameKey(entryName(entry)).replace(/\s+/g, "_")}`;
}

/**
 * Drops entries without a usable name and every entry whose e-mail appears more than once.
 * Returns { accepted: [{ entry, index }], rejected: { no_name, duplicate_email } } (counts only).
 */
export function validateImportEntries(entries = []) {
  const emailCount = new Map();
  for (const e of entries) {
    const email = String(e?.email || "").toLowerCase().trim();
    if (email) emailCount.set(email, (emailCount.get(email) || 0) + 1);
  }
  const accepted = [];
  const rejected = { no_name: 0, duplicate_email: 0 };
  entries.forEach((entry, index) => {
    if (!normalizeNameKey(entryName(entry))) {
      rejected.no_name += 1;
      return;
    }
    const email = String(entry?.email || "").toLowerCase().trim();
    if (email && emailCount.get(email) > 1) {
      rejected.duplicate_email += 1;
      return;
    }
    accepted.push({ entry, index });
  });
  return { accepted, rejected };
}

/**
 * Strict match: exact e-mail (case-insensitive), else exactly one user with the same
 * normalized name. Partial name matches are "ambiguous" and never applied.
 */
export function matchImportEntry(entry, users = []) {
  const email = String(entry?.email || "").toLowerCase().trim();
  if (email) {
    const byEmail = users.filter((u) => (u.email || "").toLowerCase().trim() === email);
    if (byEmail.length === 1) return { user: byEmail[0], method: "email", confidence: CONFIDENCE.HIGH };
    if (byEmail.length > 1) return { user: null, method: "ambiguous", confidence: CONFIDENCE.LOW };
  }
  const key = normalizeNameKey(entryName(entry));
  if (key) {
    const exact = users.filter((u) => u.name_key === key);
    if (exact.length === 1) return { user: exact[0], method: "name", confidence: CONFIDENCE.MEDIUM };
    if (exact.length > 1) return { user: null, method: "ambiguous", confidence: CONFIDENCE.LOW };
    const partial = users.filter((u) => u.name_key && (u.name_key.includes(key) || key.includes(u.name_key)));
    if (partial.length) return { user: null, method: "ambiguous", confidence: CONFIDENCE.LOW };
  }
  return { user: null, method: "unmatched", confidence: CONFIDENCE.LOW };
}

function folderDocId(docType, sourceFile) {
  return `${docType || "other"}_${String(sourceFile || "app").replace(/[^a-z0-9]/gi, "_").slice(0, 40)}`;
}

/** Overrides for buildKycFillPlan from one scan entry (same document ids as the legacy import). */
export function buildImportOverrides(entry, confidence) {
  const cg = entry.contract_guess || null;
  const ig = entry.identity_guess || null;
  const documents = (entry.files || []).map((f) => {
    const docType = f.doc_type || inferDocTypeFromPath(f.relative_path);
    return {
      id: folderDocId(docType, f.relative_path),
      payload: {
        doc_type: docType,
        status: KYC_DOC_STATUS.NEEDS_REVIEW,
        local_path: f.absolute_path,
        source_file: f.relative_path,
        file_ext: f.ext,
        is_signed: f.is_signed || false,
        expiry_date: inferExpiryDateFromFilename(f.relative_path),
        source_method: "folder_scan",
      },
    };
  });
  if (ig?.document_number || ig?.expiry_date) {
    const id = `folder_identity_${String(ig.source_file || "scan").replace(/[^a-z0-9]/gi, "_").slice(0, 40)}`;
    documents.push({
      id,
      payload: {
        doc_type: inferDocTypeFromPath(ig.source_file || ""),
        status: KYC_DOC_STATUS.NEEDS_REVIEW,
        id_number: ig.document_number,
        document_number: ig.document_number,
        expiry_date: ig.expiry_date,
        source_file: ig.source_file,
        extraction_confidence: ig.confidence,
        extraction_method: ig.method || "folder_pdf_text",
        source_method: "folder_scan",
      },
    });
  }
  return {
    profile: {
      display_name: entryName(entry),
      email: String(entry.email || "").toLowerCase().trim(),
      phone: entry.phone,
      folder_name: entry.folder_name,
      folder_path: entry.folder_path,
      account_state: entry.account_state,
      classification: entry.classification,
      client_address: cg?.client_address,
      identity_document_number: ig?.document_number,
      identity_expiry_date: ig?.expiry_date,
      match_confidence: confidence,
      contract_summary: cg
        ? {
            signature_date: cg.signature_date ?? null,
            initial_amount: cg.initial_amount ?? null,
            contract_wallet: cg.contract_wallet ?? null,
            fiddux_wallet_disclosed: cg.fiddux_wallet_disclosed ?? null,
            client_address: cg.client_address ?? null,
            confidence: cg.confidence || CONFIDENCE.LOW,
          }
        : undefined,
    },
    documents,
    contract: cg ? { ...cg, confidence: cg.confidence || CONFIDENCE.LOW } : null,
  };
}

/**
 * Aggregate preview rows ({ uid, created, filledCount, fields, skippedReason, error, isOrphan }).
 * Output has counts, generic field names and non-orphan uids only.
 */
export function aggregateFillPreview(rows = [], extraRejected = {}) {
  const summary = {
    total: rows.length,
    createUids: [],
    createOrphans: 0,
    fillProfiles: 0,
    nothingToFill: 0,
    skipped: { ...extraRejected },
    errors: 0,
    fieldsTotal: 0,
    fieldCounts: {},
  };
  for (const r of rows) {
    if (r.error) {
      summary.errors += 1;
      continue;
    }
    if (!r.filledCount) {
      if (!r.skippedReason || r.skippedReason === "nothing_to_fill") summary.nothingToFill += 1;
      else summary.skipped[r.skippedReason] = (summary.skipped[r.skippedReason] || 0) + 1;
      continue;
    }
    if (r.created) {
      if (r.isOrphan) summary.createOrphans += 1;
      else summary.createUids.push(r.uid);
    } else {
      summary.fillProfiles += 1;
    }
    summary.fieldsTotal += r.filledCount;
    for (const f of r.fields || []) {
      const label = genericFieldLabel(f);
      summary.fieldCounts[label] = (summary.fieldCounts[label] || 0) + 1;
    }
  }
  return summary;
}
